import React from 'react';
import { Users, Sparkles, CreditCard, Clock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const AboutSection = () => {
  const { t } = useLanguage();

  const features = [
    { icon: Users, ...t('about.features')[0] },
    { icon: Sparkles, ...t('about.features')[1] },
    { icon: CreditCard, ...t('about.features')[2] },
    { icon: Clock, ...t('about.features')[3] },
  ];

  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-16 sm:py-20 md:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-16">
          <span className="inline-block px-3 sm:px-4 py-1 sm:py-1.5 bg-[#00bfa5]/10 text-[#00bfa5] rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
            UNI CAR
          </span>
          <h2
            data-testid="about-title"
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#606060] mb-3 sm:mb-4 font-['Outfit'] tracking-tight"
          >
            {t('about.title')}
          </h2>
          <p
            data-testid="about-subtitle"
            className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto px-4"
          >
            {t('about.subtitle')}
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Image Side */}
          <div className="relative order-2 lg:order-1">
            <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1555583742-74999122200a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="UNI CAR Team"
                data-testid="about-image"
                className="w-full h-[280px] sm:h-[350px] md:h-[400px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/30 to-transparent" />
            </div>
            {/* Floating Badge */}
            <div className="absolute -bottom-4 -right-2 sm:-bottom-6 sm:-right-6 bg-[#00bfa5] text-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-xl hidden sm:block">
              <p className="text-2xl sm:text-4xl font-bold font-['Outfit']">10+</p>
              <p className="text-xs sm:text-sm opacity-90">années d'expérience</p>
            </div>
          </div>

          {/* Features Side */}
          <div className="space-y-4 sm:space-y-6 order-1 lg:order-2">
            <p
              data-testid="about-description"
              className="text-base sm:text-lg text-foreground leading-relaxed mb-6 sm:mb-8"
            >
              {t('about.description')}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {features.map((feature, index) => (
                <div
                  key={index}
                  data-testid={`about-feature-${index}`}
                  className="p-4 sm:p-5 rounded-xl bg-[#00bfa5]/5 border border-[#00bfa5]/20 hover:border-[#00bfa5]/40 hover:shadow-md transition-all group"
                >
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="p-2 sm:p-2.5 rounded-lg bg-[#606060] group-hover:bg-[#00bfa5] transition-colors flex-shrink-0">
                      <feature.icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#606060] mb-1 font-['Outfit'] text-sm sm:text-base">
                        {feature.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {feature.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
